/**
 * GlobalCommandPalette — global ⌘K popover with multiple tabs.
 *
 * Renamed from `ProjectSwitcher` in Phase 3d (wiki content backlog
 * §14). The Projects tab carries the original project-switcher
 * combobox; the Wiki tab is the new global doc finder powered by the
 * existing title-only `useWikiSearch`. ACL filtering is BE-side (the
 * Phase 3b Layer-2 visibility filter — backfilled onto `searchWiki`
 * in Phase 3d so Restricted docs don't leak via this surface).
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  [Projects]  [Wiki]                          │  ← tab strip
 *   ├──────────────────────────────────────────────┤
 *   │  🔍  Search projects… / Search docs…         │
 *   ├──────────────────────────────────────────────┤
 *   │  Recently visited / Projects                 │  (Projects tab)
 *   │   ●  [MOTE]  3mote (dogfooding)        ✓     │
 *   │                                              │
 *   │  📄 Onboarding playbook                      │  (Wiki tab)
 *   │     Engineering › Onboarding                  │  ← breadcrumb
 *   │  📁 Engineering › Runbooks                    │
 *   ├──────────────────────────────────────────────┤
 *   │  + Create project    (Projects tab only)     │
 *   └──────────────────────────────────────────────┘
 *
 * Keyboard:
 *   ⌘K / Ctrl+K — toggle open from anywhere.
 *   Tab / Shift+Tab — switch tab.
 *   ↑ / ↓ — navigate rows within the active tab.
 *   Enter — select active row.
 *   Esc — close.
 *
 * Default tab on open: context-aware. Wiki when the current route
 * starts with `/wiki`; Projects everywhere else.
 *
 * Built inline rather than via `<Dropdown>` because the popover needs
 * a search input, multiple sections, and a footer action — the
 * existing primitive is single-select / static-options.
 */

import { useLocation, useNavigate } from '@tanstack/react-router'
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import type { Project, WikiNode } from '@/api/types-bridge'
import { useTenantPermissions } from '@/features/me/use-tenant-permissions'
import { ProjectCreateModal } from '@/features/projects/components/ProjectCreateModal'
import type { WikiSearchResultRow } from '@/features/wiki/hooks/use-wiki-search'
import { useWikiSearch } from '@/features/wiki/hooks/use-wiki-search'
import { useWikiTree } from '@/features/wiki/hooks/use-wiki-tree'
import { renderSearchSnippet } from '@/features/wiki/lib/render-search-snippet'
import { buildDocPath } from '@/features/wiki/lib/slug'
import { dotColorFor } from '@/lib/project-color'
import { useRecentlyVisitedProjects } from '@/lib/recently-visited-projects'

/* -------------------------------------------------------------------------- */
/*                                 props                                      */
/* -------------------------------------------------------------------------- */

interface GlobalCommandPaletteProps {
  /**
   * Authoritative project list (from `useProjects()`). Empty array =
   * loading or no projects; the popover renders a graceful empty state.
   */
  projects: readonly Project[]
  /**
   * Currently active project (derived from the route's `projectId`
   * param). `null` on `/`, `/timesheet`, etc. Drives the trigger label.
   */
  activeProject: Project | null
}

type PaletteTab = 'projects' | 'wiki'

/* -------------------------------------------------------------------------- */
/*                              tiny SVG icons                                */
/* -------------------------------------------------------------------------- */

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function DocIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/*                              row primitives                                */
/* -------------------------------------------------------------------------- */

function ProjectColorDot({ seed }: { seed: string }) {
  return (
    <span
      aria-hidden="true"
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: dotColorFor(seed) }}
    />
  )
}

function ProjectCodeChip({ code, seed }: { code: string; seed: string }) {
  if (!code) return null
  return (
    <span
      className="inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase leading-none tabular-nums"
      style={{
        background: `hsl(${hashSeedTone(seed)} 85% 96%)`,
        color: `hsl(${hashSeedTone(seed)} 55% 30%)`,
        borderColor: `hsl(${hashSeedTone(seed)} 60% 75%)`,
      }}
    >
      {code}
    </span>
  )
}

// Inline tiny re-implementation of hashHue to avoid a circular import
// while keeping the chip color in sync with `dotColorFor` (which both
// live in `lib/project-color.ts`). Faster than re-importing two
// helpers.
function hashSeedTone(seed: string): number {
  let hash = 0
  for (let i = 0; i < (seed || 'default').length; i += 1) {
    hash = (hash * 31 + (seed || 'default').charCodeAt(i)) | 0
  }
  return (Math.abs(hash) * 137.508) % 360
}

/* -------------------------------------------------------------------------- */
/*                          projects-tab row list                             */
/* -------------------------------------------------------------------------- */

interface ProjectNavRow {
  type: 'header' | 'project'
  /** project rows only */
  project?: Project
  /** header rows only */
  label?: string
}

interface BuildProjectRowsArgs {
  projects: readonly Project[]
  recentIds: readonly string[]
  query: string
  recentlyVisitedLabel: string
  allProjectsLabel: string
}

/**
 * Build the flat row list shown in the Projects tab. Recent section
 * uses the order from the recents log; the All section is alphabetical
 * by name. Section headers are included in the flat list so the
 * renderer can walk one array.
 */
function buildProjectRows({
  projects,
  recentIds,
  query,
  recentlyVisitedLabel,
  allProjectsLabel,
}: BuildProjectRowsArgs): ProjectNavRow[] {
  const q = query.trim().toLowerCase()
  const matches = (p: Project) => {
    if (!q) return true
    const name = p.name.toLowerCase()
    const code = (p.code ?? '').toLowerCase()
    return name.includes(q) || code.includes(q)
  }

  // Dedupe by id — defensive; BE has a unique constraint
  // (`project_users` PK on `(project_id, user_id)`) and `/api/projects`
  // semi-joins via subquery so cardinality is structurally bounded to
  // one row per `projects.id` (follow-up #95). This dedupe is
  // double-coverage: keying by `id` in the renderer alone isn't enough
  // because React's reconciler treats duplicate keys as ambiguous and
  // stale DOM can stick around across renders.
  const byId = new Map<string, Project>()
  for (const p of projects) {
    if (!byId.has(p.id)) byId.set(p.id, p)
  }
  const uniqueProjects = Array.from(byId.values())

  const recents: Project[] = []
  for (const id of recentIds) {
    const p = byId.get(id)
    if (p && matches(p)) recents.push(p)
    if (recents.length >= 3) break
  }

  const all = uniqueProjects
    .filter(matches)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

  const rows: ProjectNavRow[] = []
  if (recents.length > 0 && !q) {
    rows.push({ type: 'header', label: recentlyVisitedLabel })
    for (const p of recents) rows.push({ type: 'project', project: p })
    rows.push({ type: 'header', label: allProjectsLabel })
    for (const p of all) rows.push({ type: 'project', project: p })
  } else {
    // When searching, skip the recent section — the All list ranks
    // matches once and that's the cleanest UX.
    for (const p of all) rows.push({ type: 'project', project: p })
  }
  return rows
}

/* -------------------------------------------------------------------------- */
/*                          wiki-tab breadcrumb path                          */
/* -------------------------------------------------------------------------- */

/**
 * Walk the parentId chain in the tree map to produce a breadcrumb
 * string like "Engineering › Runbooks". Excludes the node itself —
 * the row's primary line shows the name, and this is the
 * disambiguating context. 64-depth guard mirrors the BE cycle cap.
 */
function buildBreadcrumb(nodesById: Map<string, WikiNode>, nodeId: string): string {
  const parts: string[] = []
  const start = nodesById.get(nodeId)
  if (!start) return ''
  let currentId: string | null = start.parentId
  let depth = 0
  while (currentId && depth < 64) {
    const node = nodesById.get(currentId)
    if (!node) break
    parts.unshift(node.name)
    currentId = node.parentId
    depth += 1
  }
  return parts.join(' › ')
}

/* -------------------------------------------------------------------------- */
/*                          GlobalCommandPalette                              */
/* -------------------------------------------------------------------------- */

export function GlobalCommandPalette({ projects, activeProject }: GlobalCommandPaletteProps) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const recents = useRecentlyVisitedProjects()
  // Project creation is owner/admin-only on the BE. The footer action is
  // the palette's only write, so for everyone else the popover is a pure
  // navigator and the footer strip disappears with it.
  const { canCreateProjects } = useTenantPermissions()
  const recentIds = useMemo(() => recents.map((r) => r.projectId), [recents])

  const [open, setOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [activeTab, setActiveTab] = useState<PaletteTab>('projects')

  const generatedId = useId()
  const triggerId = `command-palette-trigger-${generatedId}`
  const popoverId = `command-palette-popover-${generatedId}`
  const listboxId = `${popoverId}-listbox`
  const projectsTabId = `${popoverId}-tab-projects`
  const wikiTabId = `${popoverId}-tab-wiki`

  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  /* --- 200ms debounce on the search field ------------------------------- */
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(rawQuery), 200)
    return () => window.clearTimeout(timer)
  }, [rawQuery])

  /* --- project rows ----------------------------------------------------- */
  const recentlyVisitedLabel = t('projectSwitcher.sectionRecentlyVisited')
  const allProjectsSectionLabel = t('projectSwitcher.sectionAllProjects')
  const projectRows = useMemo(
    () =>
      buildProjectRows({
        projects,
        recentIds,
        query,
        recentlyVisitedLabel,
        allProjectsLabel: allProjectsSectionLabel,
      }),
    [projects, recentIds, query, recentlyVisitedLabel, allProjectsSectionLabel],
  )
  const projectRowIndexes = useMemo(
    () =>
      projectRows.reduce<number[]>((acc, r, i) => {
        if (r.type === 'project') acc.push(i)
        return acc
      }, []),
    [projectRows],
  )

  /* --- wiki rows -------------------------------------------------------- */
  // Only fetch wiki results when the wiki tab is the active surface —
  // mounting the hook unconditionally would still skip the network
  // call (empty-query gate), but passing the live query through when
  // the user is on the projects tab would surface stale wiki results
  // on tab-flip.
  const wikiSearchEnabled = activeTab === 'wiki'
  const wikiSearch = useWikiSearch(wikiSearchEnabled ? query : '')
  const wikiTree = useWikiTree()
  // Phase 5 Wave 2 — partition results into title-tier (`snippet === null`)
  // and body-tier (`snippet !== null`). The BE merges them in a canonical
  // ranking; we keep that order within each tier and just split visually
  // under separate section headers. Both tiers participate in the same
  // ordinal index for keyboard navigation so `↑/↓` walks the flat list.
  const wikiRows = useMemo<readonly WikiSearchResultRow[]>(() => {
    if (!wikiSearchEnabled) return []
    return wikiSearch.data
  }, [wikiSearchEnabled, wikiSearch.data])
  const titleTierRows = useMemo<readonly WikiSearchResultRow[]>(
    () => wikiRows.filter((r) => r.snippet === null),
    [wikiRows],
  )
  const bodyTierRows = useMemo<readonly WikiSearchResultRow[]>(
    () => wikiRows.filter((r) => r.snippet !== null),
    [wikiRows],
  )

  /* --- reset active index when results change --------------------------- */
  // biome-ignore lint/correctness/useExhaustiveDependencies: rows identity captures both the project list and the query.
  useEffect(() => {
    setActiveIndex(0)
  }, [projectRows, wikiRows, activeTab])

  /* --- open / close lifecycle ------------------------------------------- */
  const openPopover = useCallback(() => {
    setOpen(true)
    // Context-aware default tab: wiki when current route is /wiki/...,
    // projects everywhere else. Picks per-open so a user who navigates
    // out of /wiki sees the projects tab next time without a stale
    // sticky preference. Deliberate; revisit if user feedback wants
    // sticky behavior.
    setActiveTab(location.pathname.startsWith('/wiki') ? 'wiki' : 'projects')
    // Focus the input on the next tick so the autoFocus + transition
    // settle without React batching them away.
    window.setTimeout(() => inputRef.current?.focus(), 0)
  }, [location.pathname])

  const closePopover = useCallback((returnFocus = true) => {
    setOpen(false)
    setRawQuery('')
    setQuery('')
    if (returnFocus) {
      // After a small tick so the unmounted popover doesn't steal it.
      window.setTimeout(() => triggerRef.current?.focus(), 0)
    }
  }, [])

  /* --- ⌘K / Ctrl+K global shortcut -------------------------------------- */
  useEffect(() => {
    const handleKey = (event: globalThis.KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey
      if (isMod && event.key.toLowerCase() === 'k') {
        // Don't hijack the shortcut while the user is in another modal
        // / form input where they'd reasonably expect ⌘K to do
        // something else (today: nothing, but be conservative).
        // We DO open over a focused input that's not part of an editor.
        const activeTag = (document.activeElement as HTMLElement | null)?.tagName
        const inEditor = (document.activeElement as HTMLElement | null)?.closest(
          '[contenteditable="true"]',
        )
        if (inEditor) return
        // We still allow opening from inside a regular input — the spec
        // calls for a global shortcut. The current modal stays mounted
        // behind the popover, which is fine.
        if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') {
          // Only suppress if the input is inside an open dialog —
          // otherwise we'd block the user from opening the switcher
          // while typing in the search field of a different popover.
          const inDialog = (document.activeElement as HTMLElement | null)?.closest(
            '[role="dialog"]',
          )
          if (inDialog) return
        }
        event.preventDefault()
        if (open) closePopover()
        else openPopover()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, openPopover, closePopover])

  /* --- click-outside close --------------------------------------------- */
  useEffect(() => {
    if (!open) return
    const handleMouseDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        closePopover(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open, closePopover])

  /* --- selection -------------------------------------------------------- */
  const selectProject = useCallback(
    (project: Project) => {
      closePopover(false)
      // No need to record the visit here — `BoardPage` records on
      // mount, which is the source of truth (deep links, refreshes,
      // direct nav from the bell, etc. all converge there).
      navigate({ to: '/board/$projectId', params: { projectId: project.id } })
    },
    [navigate, closePopover],
  )

  const selectAllProjects = useCallback(() => {
    closePopover(false)
    navigate({ to: '/' })
  }, [navigate, closePopover])

  const selectWikiNode = useCallback(
    (node: WikiNode) => {
      // A file whose bytes are still uploading is not openable — its page
      // would render a "still uploading" card that never recovers, because
      // nothing re-asks for the download once the transfer lands. The tree
      // row refuses the same click; search is the other way in, so it has
      // to refuse it too or the guard is only half a guard. Presign mints
      // the row, so a file is findable here seconds before it is readable.
      if (node.kind === 'file' && node.fileS3VersionId === null) return
      closePopover(false)
      // Docs AND file nodes use the Notion-style pretty URL; folders keep
      // the legacy `/wiki/d/:id` form because `FolderDetailPage` reads its
      // params via TanStack and isn't covered by the pretty route. The
      // node payload here carries `name` so `buildDocPath` can compose the
      // slug; for callers that only have an id the legacy redirect handles
      // the normalization. File nodes render on the doc-page surface
      // (`<FileNodePage>`) now, so they navigate exactly like docs — no
      // window-event / modal dance needed.
      if (node.kind === 'folder') {
        navigate({ to: '/wiki/d/$docId', params: { docId: node.id } })
      } else {
        navigate({
          to: '/wiki/$slugWithTail',
          params: { slugWithTail: buildDocPath(node).replace(/^\/wiki\//, '') },
        })
      }
    },
    [navigate, closePopover],
  )

  // Without an active project the user is on the master "All projects"
  // surface (`/`). The trigger renders that explicitly so the user
  // always knows where they are.
  const onAllProjects = activeProject === null

  /* --- keyboard nav inside the popover --------------------------------- */
  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closePopover()
        return
      }
      // Tab / Shift+Tab switches tabs. Native Tab focus traversal in
      // the popover only hits the search input + the create button;
      // claiming Tab for tab-switching is the established palette
      // convention (Linear, Notion, Slack all use it).
      if (event.key === 'Tab') {
        event.preventDefault()
        setActiveTab((prev) => (prev === 'projects' ? 'wiki' : 'projects'))
        return
      }
      const activeRowCount = activeTab === 'projects' ? projectRowIndexes.length : wikiRows.length
      if (activeRowCount === 0) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, activeRowCount - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (event.key === 'Home') {
        event.preventDefault()
        setActiveIndex(0)
      } else if (event.key === 'End') {
        event.preventDefault()
        setActiveIndex(activeRowCount - 1)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (activeTab === 'projects') {
          const rowIdx = projectRowIndexes[activeIndex]
          const row = rowIdx !== undefined ? projectRows[rowIdx] : undefined
          if (row?.project) selectProject(row.project)
        } else {
          const node = wikiRows[activeIndex]
          if (node) selectWikiNode(node)
        }
      }
    },
    [
      activeIndex,
      activeTab,
      projectRowIndexes,
      projectRows,
      wikiRows,
      selectProject,
      selectWikiNode,
      closePopover,
    ],
  )

  /* --- create-project flow --------------------------------------------- */
  const handleOpenCreate = useCallback(() => {
    closePopover(false)
    setCreateOpen(true)
  }, [closePopover])

  /* --- render ----------------------------------------------------------- */
  const searchPlaceholder =
    activeTab === 'projects'
      ? t('projectSwitcher.searchPlaceholder')
      : t('palette.wiki.searchPlaceholder')

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        data-testid="project-switcher-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => (open ? closePopover() : openPopover())}
        // `title` surfaces the project's description on hover so a
        // teammate can read what the project is for without leaving the
        // board (the description was removed from BoardHeader to declutter
        // the page; this tooltip is its replacement-in-place). Falls
        // back to the project name when no description is set, and to
        // a generic label when no project is active.
        title={
          activeProject
            ? activeProject.description?.trim()
              ? activeProject.description
              : activeProject.name
            : t('projectSwitcher.switchProjectTitle')
        }
        className="inline-flex h-8 max-w-[14rem] shrink-0 items-center gap-2 rounded-md border border-transparent px-2 text-sm font-medium text-fg transition-colors hover:bg-bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
      >
        {activeProject ? (
          <>
            <ProjectColorDot seed={activeProject.code ?? activeProject.name} />
            <span className="truncate">{activeProject.name}</span>
          </>
        ) : (
          // No icon on the trigger — the sidebar's Boards nav row
          // already carries the 3mote LogoMark in its active state, so
          // showing it here too is redundant chrome (David's call —
          // "the 3mote logo becomes too redundant in the current
          // scenario").
          <span className="truncate">{t('projectSwitcher.allProjectsLabel')}</span>
        )}
        <ChevronDownIcon className="text-fg-subtle" />
      </button>

      {open && (
        <div
          id={popoverId}
          // biome-ignore lint/a11y/useSemanticElements: <dialog> implies modal browser-managed open state which we don't want here — the popover is a non-modal lightweight floater that closes on click-outside / Esc.
          role="dialog"
          aria-label={t('palette.dialogAriaLabel')}
          data-testid="command-palette-popover"
          className="absolute start-0 z-40 mt-1 w-80 overflow-hidden rounded-lg border border-border bg-bg-elevated shadow-lg"
          onKeyDown={handleListKeyDown}
        >
          {/* Tab strip */}
          <div
            role="tablist"
            aria-label={t('palette.tablistAriaLabel')}
            className="flex border-b border-border"
          >
            <TabButton
              id={projectsTabId}
              controls={listboxId}
              selected={activeTab === 'projects'}
              onSelect={() => setActiveTab('projects')}
              label={t('palette.tab.projects')}
              testId="command-palette-tab-projects"
            />
            <TabButton
              id={wikiTabId}
              controls={listboxId}
              selected={activeTab === 'wiki'}
              onSelect={() => setActiveTab('wiki')}
              label={t('palette.tab.wiki')}
              testId="command-palette-tab-wiki"
            />
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <SearchIcon className="text-fg-subtle" />
            <input
              ref={inputRef}
              type="text"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder={searchPlaceholder}
              data-testid="command-palette-search"
              aria-controls={listboxId}
              aria-label={searchPlaceholder}
              className="w-full bg-transparent text-sm text-fg placeholder:text-fg-subtle focus:outline-none"
            />
            <kbd className="hidden text-[10px] font-mono text-fg-subtle sm:inline">
              {t('projectSwitcher.shortcutHint')}
            </kbd>
          </div>

          {/* "All projects" pinned row — Projects tab only. The wiki
              tab has no equivalent home affordance; users navigate via
              the rail on /wiki once they get there. */}
          {activeTab === 'projects' && !query && (
            <div className="border-b border-border py-1">
              {/* biome-ignore lint/a11y/useSemanticElements: <div role="button"> is intentional — the onMouseDown+preventDefault pattern preserves listbox focus, which a native <button> click semantic doesn't (mousedown blurs the listbox before the click handler). */}
              <div
                role="button"
                tabIndex={0}
                data-testid="project-switcher-all-projects"
                onMouseDown={(event) => {
                  event.preventDefault()
                  selectAllProjects()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    selectAllProjects()
                  }
                }}
                className={[
                  'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm transition-colors',
                  onAllProjects ? 'bg-bg-muted text-fg' : 'text-fg hover:bg-bg-muted',
                ].join(' ')}
              >
                <span className="flex-1 font-medium">{t('projectSwitcher.allProjectsLabel')}</span>
                {onAllProjects && <CheckIcon className="text-fg-muted" />}
              </div>
            </div>
          )}

          {/* Body — rows for the active tab */}
          <div
            id={listboxId}
            // biome-ignore lint/a11y/useSemanticElements: ARIA listbox pattern — native <select> can't render the search + sectioned shape we need.
            role="listbox"
            aria-label={t('palette.listboxAriaLabel')}
            aria-labelledby={activeTab === 'projects' ? projectsTabId : wikiTabId}
            data-testid="command-palette-listbox"
            tabIndex={-1}
            className="max-h-72 overflow-auto py-1"
          >
            {activeTab === 'projects' ? (
              projectRows.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-fg-subtle">
                  {t('projectSwitcher.noResults')}
                </div>
              ) : (
                <ProjectRowList
                  rows={projectRows}
                  projectRowIndexes={projectRowIndexes}
                  activeIndex={activeIndex}
                  activeProjectId={activeProject?.id ?? null}
                  onHover={setActiveIndex}
                  onSelect={selectProject}
                />
              )
            ) : wikiRows.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-fg-subtle">
                {wikiSearch.isLoading
                  ? t('palette.wiki.loading')
                  : query.trim().length > 0
                    ? t('palette.wiki.noResults')
                    : t('palette.wiki.emptyState')}
              </div>
            ) : (
              <WikiRowList
                titleTierRows={titleTierRows}
                bodyTierRows={bodyTierRows}
                activeIndex={activeIndex}
                nodesById={wikiTree.nodesById}
                titleHeading={t('palette.wiki.titleResultsHeading')}
                bodyHeading={t('palette.wiki.bodyResultsHeading')}
                snippetAriaLabel={t('palette.wiki.snippetAriaLabel')}
                onHover={setActiveIndex}
                onSelect={selectWikiNode}
              />
            )}
          </div>

          {/* Footer: Create project — Projects tab only, and only for a
              caller holding `create-projects`. The wiki tab has no
              analogous create-from-here affordance; doc creation is
              owner-of-a-folder driven via the rail UI. */}
          {activeTab === 'projects' && canCreateProjects && (
            <div className="border-t border-border">
              <button
                type="button"
                onClick={handleOpenCreate}
                data-testid="project-switcher-create"
                className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-bg-muted focus:bg-bg-muted focus:outline-none"
              >
                <PlusIcon className="text-fg-subtle" />
                {t('projectSwitcher.createProjectLabel')}
              </button>
            </div>
          )}
        </div>
      )}

      {canCreateProjects && (
        <ProjectCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                  tab button                                */
/* -------------------------------------------------------------------------- */

interface TabButtonProps {
  id: string
  controls: string
  selected: boolean
  onSelect: () => void
  label: string
  testId: string
}

function TabButton({ id, controls, selected, onSelect, label, testId }: TabButtonProps) {
  return (
    <button
      type="button"
      id={id}
      role="tab"
      aria-selected={selected}
      aria-controls={controls}
      data-testid={testId}
      // mousedown+preventDefault keeps focus inside the popover so the
      // search input stays focused; native click would blur it.
      onMouseDown={(e) => {
        e.preventDefault()
        onSelect()
      }}
      className={[
        'flex-1 px-3 py-2 text-sm font-medium transition-colors focus:outline-none',
        selected
          ? 'border-b-[3px] border-accent text-fg'
          : 'border-b-[3px] border-transparent text-fg-muted hover:text-fg',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

/* -------------------------------------------------------------------------- */
/*                            projects row list                               */
/* -------------------------------------------------------------------------- */

interface ProjectRowListProps {
  rows: ProjectNavRow[]
  projectRowIndexes: number[]
  activeIndex: number
  activeProjectId: string | null
  onHover: (rowOrdinal: number) => void
  onSelect: (project: Project) => void
}

function ProjectRowList({
  rows,
  projectRowIndexes,
  activeIndex,
  activeProjectId,
  onHover,
  onSelect,
}: ProjectRowListProps): ReactNode {
  return (
    <>
      {rows.map((row, index) => {
        if (row.type === 'header') {
          return (
            <div
              key={`header-${row.label}-${index}`}
              role="presentation"
              className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle"
            >
              {row.label}
            </div>
          )
        }
        const project = row.project
        if (!project) return null
        const ordinal = projectRowIndexes.indexOf(index)
        const isActive = ordinal === activeIndex
        const isSelected = project.id === activeProjectId
        const seed = project.code ?? project.name
        return (
          // biome-ignore lint/a11y/useFocusableInteractive: roving focus stays on the listbox; we drive selection via aria-activedescendant + Enter.
          <div
            // The same project can render in both the Recents and All
            // sections — `project.id` alone collides as a React key in
            // that case and the reconciler leaves stale DOM behind.
            // Combine with the row index so every visible row is unique.
            key={`${project.id}-${index}`}
            // biome-ignore lint/a11y/useSemanticElements: ARIA option role on a div — <option> only works inside <select>.
            role="option"
            aria-selected={isSelected}
            data-testid="project-switcher-row"
            data-project-id={project.id}
            onMouseEnter={() => onHover(ordinal)}
            onMouseDown={(event) => {
              event.preventDefault()
              onSelect(project)
            }}
            className={[
              'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
              isActive ? 'bg-bg-muted text-fg' : 'text-fg',
            ].join(' ')}
          >
            <ProjectColorDot seed={seed} />
            <ProjectCodeChip code={project.code ?? ''} seed={seed} />
            <span className="flex-1 truncate">{project.name}</span>
            {isSelected && <CheckIcon className="text-fg-muted" />}
          </div>
        )
      })}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*                              wiki row list                                 */
/* -------------------------------------------------------------------------- */

interface WikiRowListProps {
  titleTierRows: readonly WikiSearchResultRow[]
  bodyTierRows: readonly WikiSearchResultRow[]
  activeIndex: number
  nodesById: Map<string, WikiNode>
  titleHeading: string
  bodyHeading: string
  snippetAriaLabel: string
  onHover: (ordinal: number) => void
  onSelect: (node: WikiNode) => void
}

/**
 * Two-section wiki result list (Phase 5 Wave 2). Title-tier rows render
 * without a snippet (name + breadcrumb only); body-tier rows render
 * with the highlighted snippet underneath the breadcrumb. Section
 * headers only appear when BOTH tiers have rows — otherwise the
 * single-tier case stays heading-less to match the pre-Wave-2 look.
 *
 * Keyboard ordinal: rows count across BOTH tiers in display order
 * (title first, then body), so `↑/↓` walks the visible flat list.
 * The section header is non-selectable and doesn't consume an ordinal.
 */
function WikiRowList({
  titleTierRows,
  bodyTierRows,
  activeIndex,
  nodesById,
  titleHeading,
  bodyHeading,
  snippetAriaLabel,
  onHover,
  onSelect,
}: WikiRowListProps) {
  const showHeadings = titleTierRows.length > 0 && bodyTierRows.length > 0
  let ordinal = 0
  return (
    <>
      {showHeadings && titleTierRows.length > 0 && (
        <div
          role="presentation"
          className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle"
        >
          {titleHeading}
        </div>
      )}
      {titleTierRows.map((node) => {
        const rowOrdinal = ordinal
        ordinal += 1
        return (
          <WikiRow
            key={node.id}
            node={node}
            ordinal={rowOrdinal}
            isActive={rowOrdinal === activeIndex}
            breadcrumb={buildBreadcrumb(nodesById, node.id)}
            snippet={null}
            snippetAriaLabel={snippetAriaLabel}
            onHover={onHover}
            onSelect={onSelect}
          />
        )
      })}
      {showHeadings && bodyTierRows.length > 0 && (
        <div
          role="presentation"
          className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle"
        >
          {bodyHeading}
        </div>
      )}
      {bodyTierRows.map((node) => {
        const rowOrdinal = ordinal
        ordinal += 1
        return (
          <WikiRow
            key={node.id}
            node={node}
            ordinal={rowOrdinal}
            isActive={rowOrdinal === activeIndex}
            breadcrumb={buildBreadcrumb(nodesById, node.id)}
            snippet={node.snippet}
            snippetAriaLabel={snippetAriaLabel}
            onHover={onHover}
            onSelect={onSelect}
          />
        )
      })}
    </>
  )
}

interface WikiRowProps {
  node: WikiSearchResultRow
  ordinal: number
  isActive: boolean
  breadcrumb: string
  snippet: string | null
  snippetAriaLabel: string
  onHover: (ordinal: number) => void
  onSelect: (node: WikiNode) => void
}

function WikiRow({
  node,
  ordinal,
  isActive,
  breadcrumb,
  snippet,
  snippetAriaLabel,
  onHover,
  onSelect,
}: WikiRowProps) {
  // Phase 5 Wave D — file nodes get the doc-style page glyph (the
  // tree's MIME-derived icon set isn't worth pulling into a 14×14
  // palette row; the row's name + breadcrumb already disambiguate).
  const KindIcon = node.kind === 'folder' ? FolderIcon : DocIcon
  return (
    // biome-ignore lint/a11y/useFocusableInteractive: roving focus stays on the listbox; we drive selection via aria-activedescendant + Enter.
    // biome-ignore lint/a11y/useSemanticElements: ARIA option role on a div — <option> only works inside <select>.
    <div
      role="option"
      aria-selected={isActive}
      data-testid="command-palette-wiki-row"
      data-node-id={node.id}
      data-node-kind={node.kind}
      data-has-snippet={snippet !== null ? 'true' : 'false'}
      onMouseEnter={() => onHover(ordinal)}
      onMouseDown={(event) => {
        event.preventDefault()
        onSelect(node)
      }}
      className={[
        'flex cursor-pointer items-start gap-2 px-3 py-1.5 text-sm',
        isActive ? 'bg-bg-muted text-fg' : 'text-fg',
      ].join(' ')}
    >
      <KindIcon className="mt-0.5 shrink-0 text-fg-subtle" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{node.name}</span>
        {breadcrumb && <span className="truncate text-[11px] text-fg-subtle">{breadcrumb}</span>}
        {snippet && (
          <span
            aria-label={snippetAriaLabel}
            data-testid="command-palette-wiki-snippet"
            className="line-clamp-2 text-[11px] leading-snug text-fg-muted"
          >
            {renderSearchSnippet(snippet)}
          </span>
        )}
      </div>
    </div>
  )
}
